import { memo } from 'react';

import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import {
  DATE_ID_FORMAT,
  FULL_MONTH_FORMAT,
  MONTH_YEAR_ID_FORMAT,
  WEEKDAY_SHORT_FORMAT,
  YEAR_FORMAT,
} from '@/helpers';
import { cn } from '@/lib/utils';

import { PLANNER_STICKY_LEFT_PX } from '../../constants/sizes';
import { calculateCellWidth, isCompactCellWidth } from '../../helpers';
import usePlannerStore from '../../store';
import { TimelineFilterRangeBar } from '../filter-range/timeline-filter-range-bar';
import { UserInfoVisibilityPopup } from '../user-change/user-info-visibility-popup';

export const TimelineHeader = memo(() => {
  const { t } = useTranslation('authenticated.planner');
  const dates = usePlannerStore(store => store.dates);
  const zoomLevel = usePlannerStore(store => store.zoomLevel);
  const groupBy = usePlannerStore(store => store.groupBy);

  const cellWidth = calculateCellWidth(zoomLevel);
  const isCompact = isCompactCellWidth(cellWidth);

  return (
    <div
      className="top-0 flex h-[6.25rem] min-w-fit bg-background"
      style={{ position: 'sticky', left: 0, top: 0, zIndex: 15 }}
    >
      <div className="sticky left-0 top-0 z-10 w-56 min-w-[14rem] border-b border-r bg-background p-4">
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold">{t(`groupBy.${groupBy}`)}</span>
          <div className="flex">
            <UserInfoVisibilityPopup />
            {/* TODO: Sorting feature */}
          </div>
        </div>
      </div>
      {/* NOTE: must NOT be `relative` — it would become the offsetParent of the
          header-cell-* elements and shift every scroll-to-date computation by the
          sidebar width. The filter range bar anchors to the sticky header root. */}
      <div className="h-[6.25rem] flex-1">
        <div className="flex items-center">
          {/* Month */}
          {dates.map((date, i) => {
            if (date.date() === 1 || i === 0) {
              const endOfMonth = date.endOf('month');
              const daysUntilEndOfMonth = endOfMonth.diff(date, 'day') + 1;

              return (
                <div
                  key={`month-${date.format(MONTH_YEAR_ID_FORMAT)}`}
                  id={`month-${date.format(MONTH_YEAR_ID_FORMAT)}`}
                  className="relative flex h-11 items-center justify-start border-b border-r px-3 py-1 text-center text-xs font-semibold"
                  style={{
                    width: `${daysUntilEndOfMonth * cellWidth}rem`,
                    minWidth: `${daysUntilEndOfMonth * cellWidth}rem`,
                  }}
                >
                  {/* Sticky left = PLANNER_STICKY_LEFT_PX (sidebar width + gap) */}
                  <span
                    className="sticky right-2 w-fit"
                    style={{ left: `${PLANNER_STICKY_LEFT_PX}px` }}
                  >
                    {date.format(FULL_MONTH_FORMAT)} {date.format(YEAR_FORMAT)}
                  </span>
                </div>
              );
            }
            return null;
          })}
        </div>
        <div className="flex h-14">
          {dates.map(date => {
            const isToday = date.isSame(dayjs(), 'day');
            const isWeekend = date.day() === 0 || date.day() === 6;
            const isLastDayOfMonth = date.endOf('month').isSame(date, 'day');

            return (
              <div
                key={date.format(DATE_ID_FORMAT)}
                className={cn('relative border-b border-r py-2 text-center text-sm', {
                  'pointer-events-none bg-muted/50': isWeekend,
                  'border-r-2': isLastDayOfMonth,
                  'bg-success/10 dark:bg-success/20': isToday,
                })}
                id={`header-cell-${date.format(DATE_ID_FORMAT)}`}
                style={{
                  width: `${cellWidth}rem`,
                  minWidth: `${cellWidth}rem`,
                }}
              >
                <div className={cn('text-xs font-medium', isToday ? 'text-success' : '')}>
                  {date.date()}
                </div>
                <div
                  className={cn(
                    'text-[0.625rem]',
                    isToday ? 'text-success' : 'text-muted-foreground'
                  )}
                >
                  {isCompact
                    ? date.format(WEEKDAY_SHORT_FORMAT)[0]
                    : date.format(WEEKDAY_SHORT_FORMAT)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {/* Positioned against the sticky header root (its left includes the sidebar width) */}
      <TimelineFilterRangeBar />
    </div>
  );
});
